import { useCallback, useEffect, useState } from "react"

import { dequal } from "dequal/lite"

import {
  AssetMap,
  DealMap,
  SaleMap,
  TopicMap,
  useCurrentView,
} from "web/components"

import useViewOrEditTopicModal from "web/components/ViewOrEdit/ViewOrEditTopic"

import { useObjectList, useViewableInvalidation } from "web/hooks/data"

import useSelectionBroadcast from "web/hooks/behaviour/useSelectionBroadcast"

import { LocalizationContext } from "web/contexts"

export function ObjectMap({
  subjectType,
  parentSubjectType,
  parentSubjectId,
  topic,
  modalTitle,
  fetchURL,
  onItemClick,
  subjectBaseUrl,
  splitView = false,
  searchQuery,
  selectedIds,
  setSelectedIds,
  onAvailableIdsChanged,
}) {
  const { view, page, viewLocalizationContextValue } = useCurrentView()

  useViewableInvalidation()

  const { detail } = useObjectList({
    subjectType,
    parentSubjectType,
    parentSubjectId,
    searchQuery,
    topic: topic?.id,
    view,
    url: fetchURL,
    page,
    enableLocationIndex: true,
    includeLocationOrdinals: splitView,
    swrOptions: { suspense: false },
  })

  const data = detail.map((datum) => ({
    ...datum,
    selected: selectedIds.includes(datum.id),
  }))

  const viewOrEditTopicModal = useViewOrEditTopicModal({
    subjectType,
    topic,
    title: modalTitle,
  })

  function openTopicRecord(record) {
    viewOrEditTopicModal({
      recording: record,
      isCrossSubject: false,
      subjectUrl: subjectBaseUrl && `${subjectBaseUrl}/${record.subject_id}`,
    })
  }

  const handleItemClick = onItemClick || (topic ? openTopicRecord : undefined)

  function handleSelectionChanged(value) {
    if (value.selected) {
      setSelectedIds([...selectedIds, value.id])
    } else {
      setSelectedIds(selectedIds.filter((el) => el !== value.id))
    }
  }

  useEffect(() => {
    onAvailableIdsChanged?.(detail.map((datum) => datum.id))
  }, [detail, onAvailableIdsChanged])

  function refreshObjectList(event) {
    detail.refresh()

    if (event.detail?.clearSelection) {
      setSelectedIds([])
    }
  }

  useEffect(() => {
    document.addEventListener(
      "event-dispatcher:refresh-object-list",
      refreshObjectList
    )
    return () =>
      document.removeEventListener(
        "event-dispatcher:refresh-object-list",
        refreshObjectList
      )
  })

  if (topic) {
    return (
      <LocalizationContext.Provider value={viewLocalizationContextValue}>
        <TopicMap
          subjectType={subjectType}
          topic={topic}
          topicRecords={data}
          onItemClick={handleItemClick}
          onSelectionChanged={handleSelectionChanged}
          isLoading={detail.isLoading}
          showOrdinals={splitView}
        />
      </LocalizationContext.Provider>
    )
  }

  switch (subjectType) {
    case "asset":
      return (
        <LocalizationContext.Provider value={viewLocalizationContextValue}>
          <AssetMap
            assets={data}
            onSelectionChanged={handleSelectionChanged}
            onItemClick={onItemClick}
            isLoading={detail.isLoading}
            showOrdinals={splitView}
          />
        </LocalizationContext.Provider>
      )
    case "deal":
      return (
        <LocalizationContext.Provider value={viewLocalizationContextValue}>
          <DealMap
            deals={data}
            onSelectionChanged={handleSelectionChanged}
            onItemClick={onItemClick}
            isLoading={detail.isLoading}
            showOrdinals={splitView}
          />
        </LocalizationContext.Provider>
      )
    case "sale":
      return (
        <LocalizationContext.Provider value={viewLocalizationContextValue}>
          <SaleMap
            sales={data}
            onSelectionChanged={handleSelectionChanged}
            onItemClick={onItemClick}
            isLoading={detail.isLoading}
            showOrdinals={splitView}
          />
        </LocalizationContext.Provider>
      )
    default:
      return null
  }
}

export default function StandaloneObjectMap({
  enableSelection = false,
  ...props
}) {
  const [selectedIds, setSelectedIds] = useState([])
  const [availableIds, setAvailableIds] = useState([])

  const handleAvailableIdsChanged = useCallback((ids) => {
    setAvailableIds((previous) => (dequal(previous, ids) ? previous : ids))
  }, [])

  const visibleSelectedIds = selectedIds.filter((id) =>
    availableIds.includes(id)
  )

  useSelectionBroadcast({ enableSelection, selectedIds: visibleSelectedIds })

  return (
    <ObjectMap
      {...props}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      onAvailableIdsChanged={handleAvailableIdsChanged}
    />
  )
}
